import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Users,
  BookOpen,
  DollarSign,
  UserCheck,
  Shield,
  ShieldAlert,
  CheckCircle,
  Clock,
  Star,
  TrendingUp,
  AlertTriangle,
  Edit,
  FileText,
  Plus,
  Trash2,
  Upload,
  Calendar,
  Eye,
  Settings,
  Image,
  PlusCircle,
  UserCog,
  Archive,
  ArchiveRestore,
  Settings as SettingsIcon,
  Search,
  UserPlus,
  Percent,
  Mail,
  Phone,
  Bell,
} from 'lucide-react';
import type {
  User,
  PartnerApplication,
  PageContent,
  Creator,
  Class,
  Category,
  PromoCode,
} from '@shared/schema';
import type { PaginatedResponse, AdminCreatorDto } from '@shared/api-contracts';
import {
  type CancellationPolicy,
  CANCELLATION_POLICY_OPTIONS,
  DEFAULT_CANCELLATION_POLICY,
} from '@shared/cancellation-policy';
import {
  NO_REGISTRATION_CUTOFF,
  REGISTRATION_CUTOFF_OPTIONS,
  registrationCutoffFromFormValue,
  registrationCutoffToFormValue,
} from '@shared/registration-cutoff';
import {
  DELIVERY_MODES,
  DELIVERY_MODE_LABELS,
  requiresLocalityForDeliveryMode,
  type DeliveryMode,
} from '@shared/delivery-mode';
import { PRICING_MODES, PRICING_MODE_LABELS, type PricingMode } from '@shared/pricing-mode';
import type { UploadResult } from '@uppy/core';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { authedJson } from '@/lib/http';
import { fetchAllPhysicalLocalities, type LocalityOption } from '@/lib/geography';
import { PublicShell } from '@/components/public-shell';
import { displayPhoneNumber } from '@/lib/phoneFormat';
import { formatDateWithTimezone, formatDateCompact, formatTimeRange } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { SharedPassGate } from '@/components/shared-pass-gate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ObjectUploader } from '@/components/ObjectUploader';
import { DateTimePicker } from '@/components/ui/datetime-picker';

// Helper function to convert UTC datetime to local datetime string for datetime-local input
function utcToLocalDatetimeString(utcDateString: string): string {
  const date = new Date(utcDateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

type AdminCreatorRow = AdminCreatorDto;

type RecurrenceFreq = 'weekly' | 'biweekly' | 'monthly';
const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
const WEEKDAY_LABELS: Record<string, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

function buildRRule(freq: RecurrenceFreq, days: string[]): string {
  if (freq === 'monthly') return 'FREQ=MONTHLY';
  const byDay = days.length > 0 ? `;BYDAY=${days.join(',')}` : '';
  if (freq === 'biweekly') return `FREQ=WEEKLY;INTERVAL=2${byDay}`;
  return `FREQ=WEEKLY${byDay}`;
}

type ClassFormState = {
  creatorId: string;
  categoryId: string;
  title: string;
  description: string;
  price: string;
  pricingMode: PricingMode;
  suggestedPrice: string;
  promoEnabled: boolean;
  promoPrice: string;
  passEnabled: boolean;
  passSessionCount: string;
  passPrice: string;
  passExpiryDays: string;
  thumbnailUrl: string;
  videoUrl: string;
  duration: string;
  cancellationPolicy: CancellationPolicy;
  /** Select value; converted to hours (or null) on submit. */
  registrationCutoffHours: string;
  registrationCutoffOnlyIfEmpty: boolean;
  difficulty: string;
  deliveryMode: DeliveryMode;
  localityId: string;
  isPublished: boolean;
};

export function AdminClassManagement() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { toast } = useToast();
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showUserSearchDialog, setShowUserSearchDialog] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUserToPromote, setSelectedUserToPromote] = useState<User | null>(null);
  const [selectedPage, setSelectedPage] = useState<string>('landing');
  const [editingContent, setEditingContent] = useState<PageContent | null>(null);
  const [contentForm, setContentForm] = useState({
    sectionKey: '',
    contentValue: '',
    contentType: 'text',
  });
  const [editingCreator, setEditingCreator] = useState<Creator | null>(null);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [isAddingPromoCode, setIsAddingPromoCode] = useState(false);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
  });
  const [creatorForm, setCreatorForm] = useState({
    displayName: '',
    creatorHeadline: '',
    bio: '',
    experience: '',
    portfolioUrl: '',
    profileImageUrl: '',
    socialLinks: {
      instagram: '',
      twitter: '',
    },
    isVerified: false,
    isPartner: false,
    isActive: true,
  });
  const [classForm, setClassForm] = useState<ClassFormState>({
    creatorId: '',
    categoryId: '',
    title: '',
    description: '',
    price: '',
    pricingMode: 'standard',
    suggestedPrice: '',
    promoEnabled: false,
    promoPrice: '',
    passEnabled: false,
    passSessionCount: '',
    passPrice: '',
    passExpiryDays: '',
    thumbnailUrl: '',
    videoUrl: '',
    duration: '',
    cancellationPolicy: DEFAULT_CANCELLATION_POLICY,
    registrationCutoffHours: NO_REGISTRATION_CUTOFF,
    registrationCutoffOnlyIfEmpty: false,
    difficulty: 'beginner',
    deliveryMode: 'in_person',
    localityId: '',
    isPublished: false,
  });

  // User Management State (from super admin dashboard)
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    [key: string]: unknown;
  } | null>(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user',
  });
  const [userForm, setUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    streetAddress: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    role: 'user',
    isActive: true,
  });

  // Partner Applications State
  const [selectedApplication, setSelectedApplication] = useState<PartnerApplication | null>(null);
  const [isViewingApplication, setIsViewingApplication] = useState(false);

  // Class Management State
  const [managingSessionsFor, setManagingSessionsFor] = useState<Class | null>(null);
  const [editingSession, setEditingSession] = useState<any>(null);
  const [sessionForm, setSessionForm] = useState({
    startDateTime: '',
    endDateTime: '',
    availableSpots: '',
    sessionNotes: '',
    isRecurring: false,
    recurrenceFreq: 'weekly' as RecurrenceFreq,
    recurrenceDays: [] as string[],
    recurrenceEndsAt: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    type: string;
    id: string;
    name: string;
  } | null>(null);
  const [viewingEnrollments, setViewingEnrollments] = useState<any>(null);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      redirectToLogin(500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  // Fetch all users for admin management (server-side search, debounced)
  const [usersListSearch, setUsersListSearch] = useState('');
  const [debouncedUsersSearch, setDebouncedUsersSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUsersSearch(usersListSearch), 300);
    return () => clearTimeout(t);
  }, [usersListSearch]);

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['/api/admin/users', debouncedUsersSearch],
    queryFn: () =>
      authedJson<User[]>(
        `/api/admin/users?limit=100${
          debouncedUsersSearch.trim()
            ? `&search=${encodeURIComponent(debouncedUsersSearch.trim())}`
            : ''
        }`
      ),
    enabled: !!user && user.role === 'admin',
  });

  // Fetch existing data
  const { data: stats } = useQuery<{
    totalUsers?: number;
    totalClasses?: number;
    totalCreators?: number;
    totalVenues?: number;
    totalAmbassadors?: number;
    totalEnrollments?: number;
    revenue?: number;
  }>({
    queryKey: ['/api/admin/stats'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: partnerApplications = [] } = useQuery({
    queryKey: ['/api/admin/partner-applications'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: pageContentData = [] } = useQuery({
    queryKey: [`/api/admin/content/${selectedPage}`],
    enabled: !!user && user.role === 'admin',
  });

  const { data: creatorsPage } = useQuery<PaginatedResponse<AdminCreatorRow>>({
    queryKey: ['/api/admin/creators'],
    enabled: !!user && user.role === 'admin',
  });
  const creators = creatorsPage?.data ?? [];

  const { data: classesPage } = useQuery<PaginatedResponse<Class>>({
    queryKey: ['/api/admin/classes'],
    enabled: !!user && user.role === 'admin',
  });
  const classes = classesPage?.data ?? [];

  const { data: adminSchedule = [], isLoading: scheduleLoading } = useQuery({
    queryKey: ['/api/admin/schedule'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: adminArchive = [], isLoading: archiveLoading } = useQuery({
    queryKey: ['/api/admin/archive'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['/api/categories'],
    enabled: !!user && user.role === 'admin',
  });

  const { data: localities = [] } = useQuery<LocalityOption[]>({
    queryKey: ['all-physical-localities'],
    queryFn: fetchAllPhysicalLocalities,
    staleTime: 1000 * 60 * 10,
  });

  // Create new category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      return await apiRequest('POST', '/api/categories', data);
    },
    onSuccess: (newCategory: any) => {
      toast({
        title: 'Category Created',
        description: `New category "${newCategory.name}" has been created successfully.`,
      });
      setIsCreatingCategory(false);
      setNewCategory({ name: '', description: '' });
      // Refresh categories list
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      // Auto-select the new category in the class form
      setClassForm(prev => ({ ...prev, categoryId: newCategory.id }));
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Category Creation Failed',
        description: error.message || 'Failed to create category. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Fetch session enrollment details
  const { data: sessionEnrollments, isLoading: enrollmentsLoading } = useQuery({
    queryKey: [`/api/sessions/${viewingEnrollments?.sessionId}/enrollments`],
    enabled: !!viewingEnrollments?.sessionId,
    queryFn: async () => {
      return await authedJson(`/api/sessions/${viewingEnrollments?.sessionId}/enrollments`);
    },
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest('POST', '/api/admin/users', data);
    },
    onSuccess: () => {
      toast({
        title: 'User Created',
        description: 'New user has been successfully created.',
      });
      setIsAddingUser(false);
      setNewUser({
        email: '',
        firstName: '',
        lastName: '',
        role: 'user',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/stats'] });
    },
    onError: error => {
      toast({
        title: 'Creation Failed',
        description: 'Failed to create user. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update user role mutation
  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      return await apiRequest('PUT', `/api/admin/users/${userId}/role`, { role });
    },
    onSuccess: () => {
      toast({
        title: 'Role Updated',
        description: 'User role has been successfully updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update user role. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update user status mutation
  const toggleAmbassadorMutation = useMutation({
    mutationFn: async ({ userId, next }: { userId: string; next: boolean }) => {
      return next
        ? await apiRequest('POST', '/api/admin/financials/ambassadors/grant', {
            userIdOrEmail: userId,
          })
        : await apiRequest('POST', `/api/admin/financials/ambassadors/${userId}/revoke`);
    },
    onSuccess: () => {
      toast({ title: 'Ambassador access updated' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: () => toast({ title: 'Could not update ambassador access', variant: 'destructive' }),
  });

  const updateUserStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      return await apiRequest('PUT', `/api/admin/users/${userId}/status`, { isActive });
    },
    onSuccess: () => {
      toast({
        title: 'Status Updated',
        description: 'User status has been successfully updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update user status. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update user profile mutation
  const updateUserProfileMutation = useMutation({
    mutationFn: async ({ userId, userData }: { userId: string; userData: any }) => {
      return await apiRequest('PUT', `/api/admin/users/${userId}`, userData);
    },
    onSuccess: () => {
      toast({
        title: 'Profile Updated',
        description: 'User profile has been successfully updated.',
      });
      setIsEditingUser(false);
      setEditingUser(null);
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update user profile. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest('DELETE', `/api/admin/users/${userId}`);
    },
    onSuccess: () => {
      toast({
        title: 'User Deleted',
        description: 'User has been successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/stats'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete user. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const approveApplicationMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      return await apiRequest('PUT', `/api/admin/partner-applications/${applicationId}/status`, {
        status: 'approved',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Partner Application Approved',
        description:
          'This marks the partnership inquiry as approved. Creator access still requires creator application approval.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/partner-applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Approval Failed',
        description: 'Failed to approve application. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteApplicationMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      return await apiRequest('DELETE', `/api/admin/partner-applications/${applicationId}`);
    },
    onSuccess: () => {
      toast({
        title: 'Application Deleted',
        description: 'Partner application has been successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/partner-applications'] });
      setIsViewingApplication(false);
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete application. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateContentMutation = useMutation({
    mutationFn: async (data: {
      pageSlug: string;
      sectionKey: string;
      contentValue: string;
      contentType: string;
    }) => {
      return await apiRequest('PUT', `/api/admin/content/${data.pageSlug}/${data.sectionKey}`, {
        contentValue: data.contentValue,
        contentType: data.contentType,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Content Updated',
        description: 'Page content has been successfully updated.',
      });
      setEditingContent(null);
      queryClient.invalidateQueries({ queryKey: [`/api/admin/content/${selectedPage}`] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update content. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateCreatorMutation = useMutation({
    mutationFn: async (data: { id: string; creator: any }) => {
      return await apiRequest('PUT', `/api/admin/creators/${data.id}`, data.creator);
    },
    onSuccess: () => {
      toast({
        title: 'Creator Updated',
        description: 'Creator profile has been successfully updated.',
      });
      setEditingCreator(null);
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update creator. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const createCreatorMutation = useMutation({
    mutationFn: async (creator: any) => {
      return await apiRequest('POST', '/api/admin/creators', creator);
    },
    onSuccess: () => {
      toast({
        title: 'Creator Created',
        description: 'New creator profile has been successfully created.',
      });
      setEditingCreator(null);
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Creation Failed',
        description: 'Failed to create creator. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteCreatorMutation = useMutation({
    mutationFn: async (creatorId: string) => {
      return await apiRequest('DELETE', `/api/admin/creators/${creatorId}`);
    },
    onSuccess: () => {
      toast({
        title: 'Creator Deleted',
        description: 'Creator profile has been successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Deletion Failed',
        description: 'Failed to delete creator. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Class Management Mutations
  const createClassMutation = useMutation({
    mutationFn: async (data: any) => {
      const transformedData = {
        ...data,
        price: data.pricingMode === 'free' ? 0 : parseFloat(data.price) || 0,
        duration: parseInt(data.duration) || 60,
        localityId: data.localityId || null,
        pricingMode: data.pricingMode || 'standard',
        suggestedPrice:
          data.pricingMode === 'pay_what_you_want' && data.suggestedPrice
            ? parseFloat(data.suggestedPrice)
            : null,
        promoEnabled: !!data.promoEnabled,
        promoPrice:
          data.promoEnabled && data.promoPrice !== '' ? parseFloat(data.promoPrice) : null,
        passEnabled: !!data.passEnabled,
        passSessionCount:
          data.passEnabled && data.passSessionCount !== '' ? parseInt(data.passSessionCount) : null,
        passPrice:
          data.passEnabled && data.passPrice !== ''
            ? Math.round(parseFloat(data.passPrice) * 100)
            : null,
        passExpiryDays:
          data.passEnabled && data.passExpiryDays !== '' ? parseInt(data.passExpiryDays) : null,
        registrationCutoffHours: registrationCutoffFromFormValue(data.registrationCutoffHours),
        registrationCutoffOnlyIfEmpty: !!data.registrationCutoffOnlyIfEmpty,
      };
      return await apiRequest('POST', '/api/admin/classes', transformedData);
    },
    onSuccess: () => {
      toast({
        title: 'Class Created',
        description: 'New class has been successfully created.',
      });
      setEditingClass(null);
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/classes'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      let description = 'Failed to create class. Please try again.';
      try {
        const match = (error as Error).message?.match(/^\d+: (.+)$/s);
        if (match) {
          const body = JSON.parse(match[1]);
          if (body.error === 'Service unavailable') {
            description = 'The server is starting up. Please wait a moment and try again.';
          } else if (body.error) {
            description = body.error;
          }
        }
      } catch {
        // ignore parse errors
      }
      toast({ title: 'Creation Failed', description, variant: 'destructive' });
    },
  });

  const updateClassMutation = useMutation({
    mutationFn: async ({ classId, data }: { classId: string; data: any }) => {
      const transformedData = {
        ...data,
        price: data.pricingMode === 'free' ? 0 : parseFloat(data.price) || 0,
        duration: parseInt(data.duration) || 60,
        localityId: data.localityId || null,
        pricingMode: data.pricingMode || 'standard',
        suggestedPrice:
          data.pricingMode === 'pay_what_you_want' && data.suggestedPrice
            ? parseFloat(data.suggestedPrice)
            : null,
        promoEnabled: !!data.promoEnabled,
        promoPrice:
          data.promoEnabled && data.promoPrice !== '' ? parseFloat(data.promoPrice) : null,
        passEnabled: !!data.passEnabled,
        passSessionCount:
          data.passEnabled && data.passSessionCount !== '' ? parseInt(data.passSessionCount) : null,
        passPrice:
          data.passEnabled && data.passPrice !== ''
            ? Math.round(parseFloat(data.passPrice) * 100)
            : null,
        passExpiryDays:
          data.passEnabled && data.passExpiryDays !== '' ? parseInt(data.passExpiryDays) : null,
        registrationCutoffHours: registrationCutoffFromFormValue(data.registrationCutoffHours),
        registrationCutoffOnlyIfEmpty: !!data.registrationCutoffOnlyIfEmpty,
      };
      return await apiRequest('PUT', `/api/admin/classes/${classId}`, transformedData);
    },
    onSuccess: () => {
      toast({
        title: 'Class Updated',
        description: 'Class has been successfully updated.',
      });
      setEditingClass(null);
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/classes'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteClassMutation = useMutation({
    mutationFn: async (classId: string) => {
      return await apiRequest('DELETE', `/api/classes/${classId}`);
    },
    onSuccess: () => {
      toast({
        title: 'Class Deleted',
        description: 'Class has been successfully deleted.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/classes'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      // Surface the server's specific reason (e.g. "Archive it instead" for
      // classes with enrollments/orders) rather than a generic failure.
      let description = 'Failed to delete class. Please try again.';
      try {
        const match = (error as Error).message?.match(/^\d+: (.+)$/s);
        if (match) {
          const body = JSON.parse(match[1]);
          if (body.error) description = body.error;
        }
      } catch {
        // ignore parse errors, use default
      }
      toast({ title: 'Deletion Failed', description, variant: 'destructive' });
    },
  });

  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  async function handleThumbnailUpload(file: File, classId: string): Promise<void> {
    setThumbnailUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const res = await fetch(`/api/classes/${classId}/thumbnail`, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: buf,
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setClassForm(prev => ({ ...prev, thumbnailUrl: data.imageUrl }));
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/classes'] });
      toast({ title: 'Thumbnail uploaded', description: 'Class thumbnail updated.' });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload thumbnail.',
        variant: 'destructive',
      });
    } finally {
      setThumbnailUploading(false);
    }
  }

  const archiveClassMutation = useMutation({
    mutationFn: async (classId: string) => {
      return await apiRequest('PUT', `/api/classes/${classId}/archive`);
    },
    onSuccess: () => {
      toast({
        title: 'Class Archived',
        description: 'Class has been successfully archived.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/classes'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Archive Failed',
        description: 'Failed to archive class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const unarchiveClassMutation = useMutation({
    mutationFn: async (classId: string) => {
      return await apiRequest('PUT', `/api/classes/${classId}/unarchive`);
    },
    onSuccess: () => {
      toast({
        title: 'Class Unarchived',
        description: 'Class has been successfully unarchived.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/classes'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/classes'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Unarchive Failed',
        description: 'Failed to unarchive class. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Session Management Mutations
  const { data: sessions = [] } = useQuery({
    queryKey: [`/api/admin/classes/${managingSessionsFor?.id}/sessions`],
    enabled: !!managingSessionsFor,
  });

  // Waitlist for the class being managed.
  const { data: waitlist, isLoading: waitlistLoading } = useQuery<{
    entries: Array<{ id: string; email: string; name: string | null; createdAt: string }>;
    total: number;
  }>({
    queryKey: [`/api/waitlist/classes/${managingSessionsFor?.id}`],
    queryFn: async () => {
      if (!managingSessionsFor?.id) return { entries: [], total: 0 };
      return await authedJson(`/api/waitlist/classes/${managingSessionsFor.id}`);
    },
    enabled: !!managingSessionsFor,
  });

  const [notifySessionId, setNotifySessionId] = useState('');

  const notifyWaitlistMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiRequest(
        'POST',
        `/api/waitlist/classes/${managingSessionsFor?.id}/notify`,
        { sessionId }
      );
      return await response.json();
    },
    onSuccess: data => {
      toast({
        title: 'Waitlist notified',
        description: `Let ${data.notifiedCount} ${data.notifiedCount === 1 ? 'person' : 'people'} know about the new session.`,
      });
      setNotifySessionId('');
      queryClient.invalidateQueries({
        queryKey: [`/api/waitlist/classes/${managingSessionsFor?.id}`],
      });
      queryClient.invalidateQueries({ queryKey: ['/api/waitlist/counts'] });
    },
    onError: () => {
      toast({
        title: 'Could not notify waitlist',
        description: 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Per-class waitlist counts for the "All Classes" table — admins see every class.
  const { data: waitlistCounts } = useQuery<{ counts: Record<string, number> }>({
    queryKey: ['/api/waitlist/counts'],
    queryFn: async () => await authedJson('/api/waitlist/counts'),
  });

  const createSessionMutation = useMutation({
    mutationFn: async ({ classId, sessionData }: { classId: string; sessionData: any }) => {
      return await apiRequest('POST', `/api/classes/${classId}/sessions`, sessionData);
    },
    onSuccess: () => {
      toast({
        title: 'Session Created',
        description: 'New session has been successfully created.',
      });
      setEditingSession(null);
      setSessionForm({
        startDateTime: '',
        endDateTime: '',
        availableSpots: '',
        sessionNotes: '',
        isRecurring: false,
        recurrenceFreq: 'weekly',
        recurrenceDays: [],
        recurrenceEndsAt: '',
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/classes/${managingSessionsFor?.id}/sessions`],
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Creation Failed',
        description: 'Failed to create session. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateSessionMutation = useMutation({
    mutationFn: async ({ sessionId, sessionData }: { sessionId: string; sessionData: any }) => {
      return await apiRequest('PUT', `/api/sessions/${sessionId}`, sessionData);
    },
    onSuccess: () => {
      toast({
        title: 'Session Updated',
        description: 'Session has been successfully updated.',
      });
      setEditingSession(null);
      setSessionForm({
        startDateTime: '',
        endDateTime: '',
        availableSpots: '',
        sessionNotes: '',
        isRecurring: false,
        recurrenceFreq: 'weekly',
        recurrenceDays: [],
        recurrenceEndsAt: '',
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/classes/${managingSessionsFor?.id}/sessions`],
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Update Failed',
        description: 'Failed to update session. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      return await apiRequest('DELETE', `/api/sessions/${sessionId}`);
    },
    onSuccess: () => {
      toast({
        title: 'Session Canceled',
        description: 'The session was canceled and enrolled attendees were updated.',
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/admin/classes/${managingSessionsFor?.id}/sessions`],
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Cancellation Failed',
        description: 'Failed to cancel session. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const promoteToCreatorMutation = useMutation({
    mutationFn: async ({ userId, creatorData }: { userId: string; creatorData: any }) => {
      // First update user role to creator
      await apiRequest('PUT', `/api/admin/users/${userId}`, { role: 'creator' });
      // Then create creator profile
      return await apiRequest('POST', '/api/admin/creators', { userId, ...creatorData });
    },
    onSuccess: () => {
      toast({
        title: 'User Promoted',
        description: 'User has been successfully promoted to creator.',
      });
      setShowUserSearchDialog(false);
      setSelectedUserToPromote(null);
      setUserSearchQuery('');
      queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/creators'] });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Promotion Failed',
        description: 'Failed to promote user to creator. Please try again.',
        variant: 'destructive',
      });
    },
  });

  if (authLoading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        <div
          className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!isAuthenticated || !user || user.role !== 'admin') {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h1 className="text-2xl font-semibold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">
            You don't have permission to access this admin dashboard.
          </p>
        </div>
      </div>
    );
  }

  const handleCreateUser = () => {
    createUserMutation.mutate(newUser);
  };

  const handleUpdateUserRole = (userId: string, role: string) => {
    updateUserRoleMutation.mutate({ userId, role });
  };

  const handleUpdateUserStatus = (userId: string, isActive: boolean) => {
    updateUserStatusMutation.mutate({ userId, isActive });
  };

  const handleCreateCategory = (e: React.FormEvent) => {
    e.preventDefault();
    createCategoryMutation.mutate({
      name: newCategory.name,
      description: newCategory.description || undefined,
    });
  };

  return (
    <div className="page-shell py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Users &amp; Classes</h1>
        <p className="text-muted-foreground">Manage users, creators, classes, and schedules</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <div className="overflow-x-auto overscroll-x-contain">
          <TabsList className="inline-flex w-max">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="creators" className="flex items-center gap-2">
              <Star className="h-4 w-4" />
              Creators
            </TabsTrigger>
            <TabsTrigger value="classes" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Classes
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="archive" className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Archive
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">User Management</h2>
            <Dialog open={isAddingUser} onOpenChange={setIsAddingUser}>
              <DialogTrigger asChild>
                <Button data-testid="button-add-user">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>
                    Create a new user account. They will be able to log in and access the platform.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newUser.email}
                      onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="user@example.com"
                      data-testid="input-user-email"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={newUser.firstName}
                        onChange={e => setNewUser({ ...newUser, firstName: e.target.value })}
                        data-testid="input-user-firstname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={newUser.lastName}
                        onChange={e => setNewUser({ ...newUser, lastName: e.target.value })}
                        data-testid="input-user-lastname"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={newUser.role}
                      onValueChange={value => setNewUser({ ...newUser, role: value })}
                    >
                      <SelectTrigger data-testid="select-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="creator">Creator</SelectItem>
                        <SelectItem value="venue">Venue</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddingUser(false)}
                    data-testid="button-cancel-add-user"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateUser}
                    disabled={createUserMutation.isPending}
                    data-testid="button-create-user"
                  >
                    {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* User overview counts */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Users', value: stats?.totalUsers },
              { label: 'Creators', value: stats?.totalCreators },
              { label: 'Venues', value: stats?.totalVenues },
              { label: 'Ambassadors', value: stats?.totalAmbassadors },
            ].map(stat => (
              <Card key={stat.label}>
                <CardContent className="p-4">
                  <p className="text-2xl font-bold tabular-nums">{stat.value ?? '—'}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All Users
              </CardTitle>
              <CardDescription>Manage user accounts, roles, and permissions</CardDescription>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by name or email…"
                  value={usersListSearch}
                  onChange={e => setUsersListSearch(e.target.value)}
                  aria-label="Search users"
                  data-testid="input-users-search"
                />
              </div>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-4 border-primary border-t-transparent rounded-full" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {((users as User[]) || []).map((user: User) => (
                      <TableRow key={user.id} data-testid={`row-user-${user.id}`}>
                        <TableCell data-testid={`text-username-${user.id}`}>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell data-testid={`text-email-${user.id}`}>{user.email}</TableCell>
                        <TableCell>
                          <Select
                            value={user.role || 'user'}
                            onValueChange={role => handleUpdateUserRole(user.id, role)}
                            data-testid={`select-role-${user.id}`}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="creator">Creator</SelectItem>
                              <SelectItem value="venue">Venue</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={user.isActive ? 'default' : 'secondary'}
                            data-testid={`status-${user.id}`}
                          >
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className="whitespace-nowrap text-sm text-muted-foreground"
                          data-testid={`text-created-${user.id}`}
                        >
                          {user.createdAt ? formatDateCompact(user.createdAt) : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingUser(user);
                                setUserForm({
                                  firstName: user.firstName || '',
                                  lastName: user.lastName || '',
                                  email: user.email || '',
                                  phoneNumber: user.phoneNumber || '',
                                  streetAddress: user.streetAddress || '',
                                  addressLine2: user.addressLine2 || '',
                                  city: user.city || '',
                                  state: user.state || '',
                                  zipCode: user.zipCode || '',
                                  country: user.country || 'US',
                                  role: user.role || 'user',
                                  isActive: user.isActive !== false,
                                });
                                setIsEditingUser(true);
                              }}
                              data-testid={`button-edit-${user.id}`}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit Profile
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateUserStatus(user.id, !user.isActive)}
                              data-testid={`button-toggle-status-${user.id}`}
                            >
                              {user.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant={user.isAmbassador ? 'default' : 'outline'}
                              size="sm"
                              onClick={() =>
                                toggleAmbassadorMutation.mutate({
                                  userId: user.id,
                                  next: !user.isAmbassador,
                                })
                              }
                              disabled={toggleAmbassadorMutation.isPending}
                              data-testid={`button-toggle-ambassador-${user.id}`}
                            >
                              {user.isAmbassador ? 'Ambassador ✓' : 'Make ambassador'}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                if (
                                  confirm(
                                    `Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`
                                  )
                                ) {
                                  deleteUserMutation.mutate(user.id);
                                }
                              }}
                              disabled={deleteUserMutation.isPending}
                              data-testid={`button-delete-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Edit User Profile Dialog */}
          <Dialog open={isEditingUser} onOpenChange={setIsEditingUser}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Edit User Profile</DialogTitle>
                <DialogDescription>
                  Update user information, contact details, and address
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  updateUserProfileMutation.mutate({
                    userId: editingUser?.id,
                    userData: userForm,
                  });
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={userForm.firstName}
                      onChange={e => setUserForm({ ...userForm, firstName: e.target.value })}
                      required
                      data-testid="input-edit-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={userForm.lastName}
                      onChange={e => setUserForm({ ...userForm, lastName: e.target.value })}
                      required
                      data-testid="input-edit-lastname"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={userForm.email}
                    onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                    required
                    data-testid="input-edit-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={userForm.phoneNumber}
                    onChange={e => setUserForm({ ...userForm, phoneNumber: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="input-edit-phone"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Address Information</h3>

                  <div>
                    <Label htmlFor="streetAddress">Street Address</Label>
                    <Input
                      id="streetAddress"
                      value={userForm.streetAddress}
                      onChange={e => setUserForm({ ...userForm, streetAddress: e.target.value })}
                      placeholder="123 Main St"
                      data-testid="input-edit-street"
                    />
                  </div>

                  <div>
                    <Label htmlFor="addressLine2">Apartment, Suite, etc. (Optional)</Label>
                    <Input
                      id="addressLine2"
                      value={userForm.addressLine2}
                      onChange={e => setUserForm({ ...userForm, addressLine2: e.target.value })}
                      placeholder="Apt 4B"
                      data-testid="input-edit-address2"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={userForm.city}
                        onChange={e => setUserForm({ ...userForm, city: e.target.value })}
                        placeholder="Indianapolis"
                        data-testid="input-edit-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={userForm.state}
                        onChange={e => setUserForm({ ...userForm, state: e.target.value })}
                        placeholder="IN"
                        maxLength={2}
                        data-testid="input-edit-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">ZIP Code</Label>
                      <Input
                        id="zipCode"
                        value={userForm.zipCode}
                        onChange={e => setUserForm({ ...userForm, zipCode: e.target.value })}
                        placeholder="46201"
                        data-testid="input-edit-zip"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Select
                      value={userForm.country}
                      onValueChange={value => setUserForm({ ...userForm, country: value })}
                    >
                      <SelectTrigger data-testid="select-edit-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                        <SelectItem value="AU">Australia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={userForm.role}
                      onValueChange={value => setUserForm({ ...userForm, role: value })}
                    >
                      <SelectTrigger data-testid="select-edit-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="creator">Creator</SelectItem>
                        <SelectItem value="venue">Venue</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={userForm.isActive}
                      onChange={e => setUserForm({ ...userForm, isActive: e.target.checked })}
                      className="rounded border-gray-300"
                      data-testid="checkbox-edit-active"
                    />
                    <Label htmlFor="isActive">Active Account</Label>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditingUser(false)}
                    data-testid="button-cancel-edit-user"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateUserProfileMutation.isPending}
                    data-testid="button-save-user"
                  >
                    {updateUserProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="overview" className="space-y-6">
          <h2 className="text-2xl font-semibold">Platform Overview</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-total-users">
                  {(stats as any)?.totalUsers || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Classes</CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-total-classes">
                  {(stats as any)?.totalClasses || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Creators</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-total-creators">
                  {(stats as any)?.totalCreators || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" data-testid="text-total-revenue">
                  ${(stats as any)?.revenue || 0}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="creators" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Creator Management</h2>
            <Button
              onClick={() => {
                setShowUserSearchDialog(true);
                setUserSearchQuery('');
                setSelectedUserToPromote(null);
              }}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Promote User to Creator
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Creators</CardTitle>
              <CardDescription>Manage creator profiles and verification status</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Creator Profile</TableHead>
                    <TableHead>Specialty</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(creators as any[]).map((creator: any) => (
                    <TableRow key={creator.userId || creator.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {creator.userFirstName} {creator.userLastName}
                          </div>
                          <div className="text-sm text-muted-foreground">{creator.userEmail}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {creator.displayName ? (
                          <div>
                            <div className="font-medium">{creator.displayName}</div>
                            <Badge variant="default" className="mt-1">
                              Has Profile
                            </Badge>
                          </div>
                        ) : (
                          <Badge variant="outline">No Profile</Badge>
                        )}
                      </TableCell>
                      <TableCell>{creator.creatorHeadline || '—'}</TableCell>
                      <TableCell>
                        {creator.displayName ? (
                          <Badge variant={creator.isVerified ? 'default' : 'secondary'}>
                            {creator.isVerified ? 'Verified' : 'Unverified'}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">No Profile</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant={creator.isActive !== false ? 'default' : 'outline'}>
                            {creator.isActive !== false ? 'Active' : 'Inactive'}
                          </Badge>
                          {creator.isPartner && (
                            <Badge variant="secondary" className="text-xs">
                              Partner
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {creator.displayName ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingCreator(creator);
                                  setCreatorForm({
                                    displayName: creator.displayName,
                                    creatorHeadline: creator.creatorHeadline || '',
                                    bio: creator.bio || '',
                                    experience: creator.experience || '',
                                    portfolioUrl: creator.portfolioUrl || '',
                                    profileImageUrl: creator.profileImageUrl || '',
                                    socialLinks: {
                                      instagram: creator.socialLinks?.instagram || '',
                                      twitter: creator.socialLinks?.twitter || '',
                                    },
                                    isVerified: creator.isVerified || false,
                                    isPartner: creator.isPartner || false,
                                    isActive: creator.isActive !== false,
                                  });
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteCreatorMutation.mutate(creator.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                setEditingCreator(creator);
                                setCreatorForm({
                                  displayName: creator.displayName,
                                  creatorHeadline: '',
                                  bio: '',
                                  experience: '',
                                  portfolioUrl: '',
                                  profileImageUrl: '',
                                  socialLinks: {
                                    instagram: '',
                                    twitter: '',
                                  },
                                  isVerified: false,
                                  isPartner: false,
                                  isActive: true,
                                });
                              }}
                            >
                              <PlusCircle className="h-4 w-4 mr-1" />
                              Create Profile
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* User Search Dialog for Promotion */}
          <Dialog open={showUserSearchDialog} onOpenChange={setShowUserSearchDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Promote User to Creator</DialogTitle>
                <DialogDescription>
                  Search for users and promote them to creator status
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="userSearch">Search Users</Label>
                  <Input
                    id="userSearch"
                    value={userSearchQuery}
                    onChange={e => setUserSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full"
                  />
                </div>

                {/* User Search Results */}
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {Array.isArray(users) &&
                    users
                      .filter(
                        (u: User) =>
                          u.role === 'user' && // Only show regular users
                          (u.firstName?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                            u.lastName?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                            u.email?.toLowerCase().includes(userSearchQuery.toLowerCase()))
                      )
                      .map((user: User) => (
                        <div
                          key={user.id}
                          className={`p-3 border rounded-lg cursor-pointer hover:bg-muted ${
                            selectedUserToPromote?.id === user.id ? 'bg-muted border-primary' : ''
                          }`}
                          onClick={() => setSelectedUserToPromote(user)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                            <Badge variant="outline">{user.role}</Badge>
                          </div>
                        </div>
                      ))}
                </div>

                {selectedUserToPromote && (
                  <div className="p-4 border rounded-lg bg-muted">
                    <h4 className="font-medium mb-2">Selected User:</h4>
                    <p>
                      {selectedUserToPromote.firstName} {selectedUserToPromote.lastName}
                    </p>
                    <p className="text-sm text-muted-foreground">{selectedUserToPromote.email}</p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUserSearchDialog(false);
                    setSelectedUserToPromote(null);
                    setUserSearchQuery('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (selectedUserToPromote) {
                      promoteToCreatorMutation.mutate({
                        userId: selectedUserToPromote.id,
                        creatorData: {
                          displayName: `${selectedUserToPromote.firstName} ${selectedUserToPromote.lastName}`,
                          bio: '',
                          creatorHeadline: '',
                          experience: '',
                          portfolioUrl: '',
                          socialLinks: {},
                          isVerified: false,
                          isPartner: false,
                          isActive: true,
                        },
                      });
                    }
                  }}
                  disabled={!selectedUserToPromote || promoteToCreatorMutation.isPending}
                >
                  {promoteToCreatorMutation.isPending ? 'Promoting...' : 'Promote to Creator'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Creator Edit Dialog */}
          <Dialog open={!!editingCreator} onOpenChange={open => !open && setEditingCreator(null)}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingCreator ? 'Edit Creator Profile' : 'Create New Creator'}
                </DialogTitle>
                <DialogDescription>
                  {editingCreator
                    ? 'Update creator profile information'
                    : 'Create a new creator profile'}
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  if (editingCreator && editingCreator.id) {
                    updateCreatorMutation.mutate({ id: editingCreator.id, creator: creatorForm });
                  } else if (editingCreator && editingCreator.userId) {
                    // Creating new profile for existing user
                    createCreatorMutation.mutate({
                      ...creatorForm,
                      userId: editingCreator.userId,
                    });
                  } else {
                    // Creating new profile (from Add Creator button)
                    createCreatorMutation.mutate(creatorForm);
                  }
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="displayName">Display Name *</Label>
                    <Input
                      id="displayName"
                      value={creatorForm.displayName}
                      onChange={e =>
                        setCreatorForm({ ...creatorForm, displayName: e.target.value })
                      }
                      placeholder="Creator display name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="creatorHeadline">Creator Headline</Label>
                    <Input
                      id="creatorHeadline"
                      value={creatorForm.creatorHeadline}
                      onChange={e =>
                        setCreatorForm({ ...creatorForm, creatorHeadline: e.target.value })
                      }
                      placeholder="e.g., Photography, Cooking, Music"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={creatorForm.bio}
                    onChange={e => setCreatorForm({ ...creatorForm, bio: e.target.value })}
                    placeholder="Biography, teaching style, background, and other details"
                    rows={6}
                  />
                </div>

                <div>
                  <Label htmlFor="experience">Experience & Qualifications</Label>
                  <Textarea
                    id="experience"
                    value={creatorForm.experience}
                    onChange={e => setCreatorForm({ ...creatorForm, experience: e.target.value })}
                    placeholder="Describe relevant experience and qualifications"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="portfolioUrl">Website URL</Label>
                  <Input
                    id="portfolioUrl"
                    type="url"
                    value={creatorForm.portfolioUrl}
                    onChange={e => setCreatorForm({ ...creatorForm, portfolioUrl: e.target.value })}
                    onBlur={() => {
                      if (
                        creatorForm.portfolioUrl &&
                        !creatorForm.portfolioUrl.match(/^https?:\/\//)
                      ) {
                        setCreatorForm({
                          ...creatorForm,
                          portfolioUrl: `https://${creatorForm.portfolioUrl}`,
                        });
                      }
                    }}
                    placeholder="https://your-website.com"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Social Media Links</h3>
                  <div>
                    <Label htmlFor="instagram">Instagram URL</Label>
                    <Input
                      id="instagram"
                      type="url"
                      value={creatorForm.socialLinks.instagram}
                      onChange={e =>
                        setCreatorForm({
                          ...creatorForm,
                          socialLinks: { ...creatorForm.socialLinks, instagram: e.target.value },
                        })
                      }
                      onBlur={() => {
                        if (
                          creatorForm.socialLinks.instagram &&
                          !creatorForm.socialLinks.instagram.match(/^https?:\/\//)
                        ) {
                          setCreatorForm({
                            ...creatorForm,
                            socialLinks: {
                              ...creatorForm.socialLinks,
                              instagram: `https://${creatorForm.socialLinks.instagram}`,
                            },
                          });
                        }
                      }}
                      placeholder="https://instagram.com/username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="twitter">Twitter URL</Label>
                    <Input
                      id="twitter"
                      type="url"
                      value={creatorForm.socialLinks.twitter}
                      onChange={e =>
                        setCreatorForm({
                          ...creatorForm,
                          socialLinks: { ...creatorForm.socialLinks, twitter: e.target.value },
                        })
                      }
                      onBlur={() => {
                        if (
                          creatorForm.socialLinks.twitter &&
                          !creatorForm.socialLinks.twitter.match(/^https?:\/\//)
                        ) {
                          setCreatorForm({
                            ...creatorForm,
                            socialLinks: {
                              ...creatorForm.socialLinks,
                              twitter: `https://${creatorForm.socialLinks.twitter}`,
                            },
                          });
                        }
                      }}
                      placeholder="https://twitter.com/username"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isVerified"
                      checked={creatorForm.isVerified}
                      onChange={e =>
                        setCreatorForm({ ...creatorForm, isVerified: e.target.checked })
                      }
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="isVerified">Verified Creator</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="creatorIsActive"
                      checked={creatorForm.isActive}
                      onChange={e => setCreatorForm({ ...creatorForm, isActive: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="creatorIsActive">Active (publicly visible)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isPartner"
                      checked={creatorForm.isPartner}
                      onChange={e =>
                        setCreatorForm({ ...creatorForm, isPartner: e.target.checked })
                      }
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="isPartner">Partner (featured placement)</Label>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditingCreator(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateCreatorMutation.isPending || createCreatorMutation.isPending}
                  >
                    {updateCreatorMutation.isPending || createCreatorMutation.isPending
                      ? 'Saving...'
                      : editingCreator
                        ? 'Update Creator'
                        : 'Create Creator'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="classes" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Class Management</h2>
            <Button
              onClick={() => {
                setEditingClass({} as Class);
                setClassForm({
                  creatorId: '',
                  categoryId: '',
                  title: '',
                  description: '',
                  price: '',
                  pricingMode: 'standard',
                  suggestedPrice: '',
                  promoEnabled: false,
                  promoPrice: '',
                  passEnabled: false,
                  passSessionCount: '',
                  passPrice: '',
                  passExpiryDays: '',
                  thumbnailUrl: '',
                  videoUrl: '',
                  duration: '',
                  cancellationPolicy: DEFAULT_CANCELLATION_POLICY,
                  registrationCutoffHours: NO_REGISTRATION_CUTOFF,
                  registrationCutoffOnlyIfEmpty: false,
                  difficulty: 'beginner',
                  deliveryMode: 'in_person',
                  localityId: '',
                  isPublished: true,
                });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Class
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Classes</CardTitle>
              <CardDescription>Manage published and draft classes</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Creator</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Enrolled</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(classes as Class[])
                    .sort((a, b) => {
                      // Move archived classes to the bottom
                      if (a.isArchived && !b.isArchived) return 1;
                      if (!a.isArchived && b.isArchived) return -1;
                      // Within same archive status, sort by creation date (newest first)
                      return (
                        new Date(b.createdAt || '').getTime() -
                        new Date(a.createdAt || '').getTime()
                      );
                    })
                    .map((cls: Class) => (
                      <TableRow key={cls.id}>
                        <TableCell>{cls.title}</TableCell>
                        <TableCell>
                          {(() => {
                            const creator = (creators as Creator[]).find(
                              c => c.id === cls.creatorId
                            );
                            return creator ? creator.displayName : cls.creatorId;
                          })()}
                        </TableCell>
                        <TableCell>${cls.price}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              cls.isArchived ? 'outline' : cls.isPublished ? 'default' : 'secondary'
                            }
                          >
                            {cls.isArchived ? 'Archived' : cls.isPublished ? 'Published' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell>{cls.enrollmentCount || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-2 justify-end flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingClass(cls);
                                setClassForm({
                                  creatorId: cls.creatorId,
                                  categoryId: cls.categoryId || '',
                                  title: cls.title,
                                  description: cls.description || '',
                                  price: cls.price.toString(),
                                  pricingMode:
                                    ((cls as { pricingMode?: string }).pricingMode as
                                      | PricingMode
                                      | undefined) || 'standard',
                                  suggestedPrice:
                                    (cls as { suggestedPrice?: string | null }).suggestedPrice !=
                                    null
                                      ? String(
                                          (cls as { suggestedPrice?: string | null }).suggestedPrice
                                        )
                                      : '',
                                  promoEnabled:
                                    (cls as { promoEnabled?: boolean }).promoEnabled ?? false,
                                  promoPrice:
                                    (cls as { promoPrice?: string | null }).promoPrice != null
                                      ? String((cls as { promoPrice?: string | null }).promoPrice)
                                      : '',
                                  passEnabled:
                                    (cls as { passEnabled?: boolean }).passEnabled ?? false,
                                  passSessionCount:
                                    (cls as { passSessionCount?: number | null })
                                      .passSessionCount != null
                                      ? String(
                                          (cls as { passSessionCount?: number | null })
                                            .passSessionCount
                                        )
                                      : '',
                                  passPrice:
                                    (cls as { passPrice?: number | null }).passPrice != null
                                      ? (
                                          (cls as { passPrice?: number | null }).passPrice! / 100
                                        ).toFixed(2)
                                      : '',
                                  passExpiryDays:
                                    (cls as { passExpiryDays?: number | null }).passExpiryDays !=
                                    null
                                      ? String(
                                          (cls as { passExpiryDays?: number | null }).passExpiryDays
                                        )
                                      : '',
                                  thumbnailUrl: cls.thumbnailUrl || '',
                                  videoUrl: cls.videoUrl || '',
                                  duration: cls.duration?.toString() || '',
                                  cancellationPolicy:
                                    (cls.cancellationPolicy as CancellationPolicy) ||
                                    DEFAULT_CANCELLATION_POLICY,
                                  registrationCutoffHours: registrationCutoffToFormValue(
                                    cls.registrationCutoffHours
                                  ),
                                  registrationCutoffOnlyIfEmpty:
                                    cls.registrationCutoffOnlyIfEmpty ?? false,
                                  difficulty: cls.difficulty || 'beginner',
                                  deliveryMode:
                                    (cls.deliveryMode as DeliveryMode | null) || 'in_person',
                                  localityId: cls.localityId || '',
                                  isPublished: cls.isPublished,
                                });
                              }}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setManagingSessionsFor(cls)}
                            >
                              <SettingsIcon className="h-4 w-4 mr-1" />
                              Sessions
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setManagingSessionsFor(cls)}
                            >
                              <Bell className="h-4 w-4 mr-1" />
                              Waitlist
                              {!!waitlistCounts?.counts[cls.id] && (
                                <Badge variant="secondary" className="ml-1.5 px-1.5">
                                  {waitlistCounts.counts[cls.id]}
                                </Badge>
                              )}
                            </Button>
                            {cls.isArchived ? (
                              <Button
                                variant="outline"
                                size="sm"
                                title="Unarchive (make visible again)"
                                onClick={() => unarchiveClassMutation.mutate(cls.id)}
                                disabled={unarchiveClassMutation.isPending}
                              >
                                <ArchiveRestore className="h-4 w-4 mr-1" />
                                Unarchive
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                title="Archive (hide from site, keep records)"
                                onClick={() => archiveClassMutation.mutate(cls.id)}
                                disabled={archiveClassMutation.isPending}
                              >
                                <Archive className="h-4 w-4 mr-1" />
                                Archive
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              title="Delete permanently (only if no enrollments/orders)"
                              onClick={() =>
                                setShowDeleteConfirm({
                                  type: 'class',
                                  id: cls.id,
                                  name: cls.title,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Class Edit Dialog */}
          <Dialog open={!!editingClass} onOpenChange={open => !open && setEditingClass(null)}>
            <DialogContent className="max-w-2xl flex flex-col max-h-[90dvh] p-0">
              <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
                <DialogTitle>{editingClass?.id ? 'Edit Class' : 'Create New Class'}</DialogTitle>
                <DialogDescription>
                  {editingClass?.id ? 'Update class information' : 'Create a new class'}
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  if (editingClass?.id) {
                    updateClassMutation.mutate({ classId: editingClass.id, data: classForm });
                  } else {
                    createClassMutation.mutate(classForm);
                  }
                }}
                className="flex flex-col flex-1 min-h-0"
              >
                <div className="flex-1 overflow-y-auto overscroll-y-contain px-6 py-4 space-y-4">
                  <div>
                    <Label htmlFor="title">Class Title *</Label>
                    <Input
                      id="title"
                      value={classForm.title}
                      onChange={e => setClassForm({ ...classForm, title: e.target.value })}
                      placeholder="Class title"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="admin-pricing-mode">Pricing</Label>
                      <Select
                        value={classForm.pricingMode}
                        onValueChange={value =>
                          setClassForm({ ...classForm, pricingMode: value as PricingMode })
                        }
                      >
                        <SelectTrigger id="admin-pricing-mode">
                          <SelectValue placeholder="Select pricing type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRICING_MODES.map(mode => (
                            <SelectItem key={mode} value={mode}>
                              {PRICING_MODE_LABELS[mode]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {classForm.pricingMode !== 'free' && (
                      <div>
                        <Label htmlFor="price">
                          {classForm.pricingMode === 'pay_what_you_want'
                            ? 'Suggested Price ($)'
                            : 'Price ($) *'}
                        </Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={
                            classForm.pricingMode === 'pay_what_you_want'
                              ? classForm.suggestedPrice
                              : classForm.price
                          }
                          onChange={e =>
                            classForm.pricingMode === 'pay_what_you_want'
                              ? setClassForm({ ...classForm, suggestedPrice: e.target.value })
                              : setClassForm({ ...classForm, price: e.target.value })
                          }
                          placeholder={
                            classForm.pricingMode === 'pay_what_you_want' ? 'Optional' : '0.00'
                          }
                          required={classForm.pricingMode === 'standard'}
                        />
                      </div>
                    )}
                  </div>
                  {classForm.pricingMode === 'standard' && (
                    <div className="space-y-3 border rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="admin-promo-enabled"
                          title="Set promo price"
                          checked={classForm.promoEnabled}
                          onChange={e =>
                            setClassForm({ ...classForm, promoEnabled: e.target.checked })
                          }
                          className="h-4 w-4"
                        />
                        <Label htmlFor="admin-promo-enabled" className="cursor-pointer font-medium">
                          Set promo price
                        </Label>
                      </div>
                      {classForm.promoEnabled && (
                        <div>
                          <Label htmlFor="admin-promo-price">Promo price ($)</Label>
                          <Input
                            id="admin-promo-price"
                            type="number"
                            step="0.01"
                            min="0"
                            value={classForm.promoPrice}
                            onChange={e =>
                              setClassForm({ ...classForm, promoPrice: e.target.value })
                            }
                            placeholder="e.g. 0 for free, or a discounted price"
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Full price shows crossed out next to this discounted price. Enter 0 to
                            offer it free.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <SharedPassGate classId={editingClass?.id}>
                    <div className="space-y-3 border rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="admin-pass-enabled"
                          title="Offer punch passes"
                          checked={classForm.passEnabled}
                          onChange={e =>
                            setClassForm({ ...classForm, passEnabled: e.target.checked })
                          }
                          className="h-4 w-4"
                        />
                        <Label htmlFor="admin-pass-enabled" className="cursor-pointer font-medium">
                          Offer punch passes
                        </Label>
                      </div>
                      {classForm.passEnabled && (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <Label htmlFor="admin-pass-session-count">Sessions per pass</Label>
                            <Input
                              id="admin-pass-session-count"
                              type="number"
                              min="2"
                              max="100"
                              value={classForm.passSessionCount}
                              onChange={e =>
                                setClassForm({ ...classForm, passSessionCount: e.target.value })
                              }
                              placeholder="e.g. 10"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="admin-pass-price">Pass price ($)</Label>
                            <Input
                              id="admin-pass-price"
                              type="number"
                              min="0"
                              step="0.01"
                              value={classForm.passPrice}
                              onChange={e =>
                                setClassForm({ ...classForm, passPrice: e.target.value })
                              }
                              placeholder="e.g. 80"
                              required
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="admin-pass-expiry">
                              Expires after (days, optional)
                            </Label>
                            <Input
                              id="admin-pass-expiry"
                              type="number"
                              min="1"
                              value={classForm.passExpiryDays}
                              onChange={e =>
                                setClassForm({ ...classForm, passExpiryDays: e.target.value })
                              }
                              placeholder="e.g. 90 — leave blank for no expiry"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </SharedPassGate>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={classForm.description}
                      onChange={e => setClassForm({ ...classForm, description: e.target.value })}
                      placeholder="Class description, agenda, materials, and what students can expect"
                      rows={6}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="duration">Duration (minutes)</Label>
                      <Input
                        id="duration"
                        value={classForm.duration}
                        onChange={e => setClassForm({ ...classForm, duration: e.target.value })}
                        placeholder="e.g., 2 hours"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cancellationPolicy">Cancellation Policy</Label>
                      <Select
                        value={classForm.cancellationPolicy}
                        onValueChange={value =>
                          setClassForm({
                            ...classForm,
                            cancellationPolicy: value as CancellationPolicy,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select cancellation policy" />
                        </SelectTrigger>
                        <SelectContent>
                          {CANCELLATION_POLICY_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {
                          CANCELLATION_POLICY_OPTIONS.find(
                            option => option.value === classForm.cancellationPolicy
                          )?.description
                        }
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="registrationCutoffHours">Registration Cutoff</Label>
                    <Select
                      value={classForm.registrationCutoffHours}
                      onValueChange={value =>
                        setClassForm({ ...classForm, registrationCutoffHours: value })
                      }
                    >
                      <SelectTrigger id="registrationCutoffHours">
                        <SelectValue placeholder="Select registration cutoff" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGISTRATION_CUTOFF_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Online booking closes this long before each session starts.
                    </p>
                    {classForm.registrationCutoffHours !== NO_REGISTRATION_CUTOFF && (
                      <div className="mt-2 flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="registrationCutoffOnlyIfEmpty"
                          className="mt-1"
                          checked={classForm.registrationCutoffOnlyIfEmpty}
                          onChange={e =>
                            setClassForm({
                              ...classForm,
                              registrationCutoffOnlyIfEmpty: e.target.checked,
                            })
                          }
                        />
                        <Label
                          htmlFor="registrationCutoffOnlyIfEmpty"
                          className="font-normal leading-snug"
                        >
                          Only close if nobody has signed up yet
                          <span className="block text-sm text-muted-foreground">
                            Once someone is coming, late sign-ups stay open until the session
                            starts.
                          </span>
                        </Label>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="deliveryMode">Delivery Mode</Label>
                      <Select
                        value={classForm.deliveryMode}
                        onValueChange={value =>
                          setClassForm(current => ({
                            ...current,
                            deliveryMode: value as DeliveryMode,
                            localityId: requiresLocalityForDeliveryMode(value as DeliveryMode)
                              ? current.localityId
                              : '',
                          }))
                        }
                      >
                        <SelectTrigger id="deliveryMode">
                          <SelectValue placeholder="Select delivery mode" />
                        </SelectTrigger>
                        <SelectContent>
                          {DELIVERY_MODES.map(mode => (
                            <SelectItem key={mode} value={mode}>
                              {DELIVERY_MODE_LABELS[mode]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {requiresLocalityForDeliveryMode(classForm.deliveryMode) && (
                      <div>
                        <Label htmlFor="localityId">Locality</Label>
                        <Select
                          value={classForm.localityId}
                          onValueChange={value =>
                            setClassForm(current => ({ ...current, localityId: value }))
                          }
                        >
                          <SelectTrigger id="localityId">
                            <SelectValue placeholder="Select locality" />
                          </SelectTrigger>
                          <SelectContent>
                            {localities.map(locality => (
                              <SelectItem key={locality.id} value={locality.id}>
                                {locality.name} ({locality.regionName})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="creatorId">Creator</Label>
                      <Select
                        value={classForm.creatorId}
                        onValueChange={value => setClassForm({ ...classForm, creatorId: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Creator" />
                        </SelectTrigger>
                        <SelectContent>
                          {(creators as Creator[]).map((creator: Creator) => (
                            <SelectItem key={creator.id} value={creator.id}>
                              {creator.displayName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="categoryId">Category</Label>
                      <Select
                        value={classForm.categoryId}
                        onValueChange={value => {
                          if (value === 'create-new') {
                            setIsCreatingCategory(true);
                          } else {
                            setClassForm({ ...classForm, categoryId: value });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {(categories as Category[]).map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="create-new" className="font-medium text-primary">
                            + Create New Category
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="difficulty">Difficulty</Label>
                      <Select
                        value={classForm.difficulty}
                        onValueChange={value => setClassForm({ ...classForm, difficulty: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isPublished"
                        checked={classForm.isPublished}
                        onChange={e =>
                          setClassForm({ ...classForm, isPublished: e.target.checked })
                        }
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="isPublished">Published</Label>
                    </div>
                  </div>

                  {/* Thumbnail upload — only available when editing an existing class */}
                  {editingClass?.id && (
                    <div>
                      <Label>Class Thumbnail</Label>
                      <div className="mt-1 space-y-2">
                        {classForm.thumbnailUrl && (
                          <img
                            src={classForm.thumbnailUrl}
                            alt="Class thumbnail"
                            className="w-full h-32 object-cover rounded-md border"
                          />
                        )}
                        <label className="flex items-center gap-2 cursor-pointer w-fit">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={thumbnailUploading}
                            asChild
                          >
                            <span>
                              <Upload className="w-4 h-4 mr-1" />
                              {thumbnailUploading
                                ? 'Uploading...'
                                : classForm.thumbnailUrl
                                  ? 'Replace thumbnail'
                                  : 'Upload thumbnail'}
                            </span>
                          </Button>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="sr-only"
                            disabled={thumbnailUploading}
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file && editingClass.id) {
                                handleThumbnailUpload(file, editingClass.id);
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <p className="text-xs text-muted-foreground">
                          JPEG, PNG, WebP or GIF, max 5MB. Optimized to 800×600 WebP.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="px-6 py-4 border-t shrink-0">
                  <Button type="button" variant="outline" onClick={() => setEditingClass(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateClassMutation.isPending || createClassMutation.isPending}
                  >
                    {updateClassMutation.isPending || createClassMutation.isPending
                      ? 'Saving...'
                      : editingClass?.id
                        ? 'Update Class'
                        : 'Create Class'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Create Category Dialog */}
          <Dialog open={isCreatingCategory} onOpenChange={setIsCreatingCategory}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Category</DialogTitle>
                <DialogDescription>
                  Add a new category that can be used for classes
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div>
                  <Label htmlFor="categoryName">Category Name</Label>
                  <Input
                    id="categoryName"
                    value={newCategory.name}
                    onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder="e.g., Music, Art, Technology"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="categoryDescription">Description (Optional)</Label>
                  <Textarea
                    id="categoryDescription"
                    value={newCategory.description}
                    onChange={e => setNewCategory({ ...newCategory, description: e.target.value })}
                    placeholder="Brief description of this category"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreatingCategory(false);
                      setNewCategory({ name: '', description: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createCategoryMutation.isPending}>
                    {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Session Management Dialog */}
          <Dialog
            open={!!managingSessionsFor}
            onOpenChange={open => !open && setManagingSessionsFor(null)}
          >
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Manage Sessions for "{managingSessionsFor?.title}"</DialogTitle>
                <DialogDescription>
                  Create, edit, and delete sessions for this class
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Waitlist */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Waitlist</CardTitle>
                    <CardDescription>
                      People waiting to hear about a new session of this class.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {waitlistLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
                      </div>
                    ) : !waitlist || waitlist.total === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No one is on the waitlist for this class yet.
                      </p>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          {waitlist.total} {waitlist.total === 1 ? 'person' : 'people'} waiting
                        </p>
                        <div className="max-h-48 overflow-y-auto space-y-2">
                          {waitlist.entries.map((entry: any) => (
                            <div
                              key={entry.id}
                              className="flex items-center justify-between text-sm border-b pb-2 last:border-0"
                            >
                              <div>
                                <p className="font-medium">{entry.name || entry.email}</p>
                                {entry.name && (
                                  <p className="text-xs text-muted-foreground">{entry.email}</p>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {formatDateCompact(entry.createdAt)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pt-2">
                          <Select value={notifySessionId} onValueChange={setNotifySessionId}>
                            <SelectTrigger className="w-auto min-w-[14rem]">
                              <SelectValue placeholder="Choose a session to announce" />
                            </SelectTrigger>
                            <SelectContent>
                              {((sessions as any[]) || [])
                                .filter(
                                  (session: any) => new Date(session.startDateTime) > new Date()
                                )
                                .map((session: any) => (
                                  <SelectItem key={session.id} value={session.id}>
                                    {formatDateWithTimezone(session.startDateTime, {
                                      includeTime: true,
                                    })}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!notifySessionId || notifyWaitlistMutation.isPending}
                            onClick={() => notifyWaitlistMutation.mutate(notifySessionId)}
                          >
                            {notifyWaitlistMutation.isPending ? 'Notifying…' : 'Notify waitlist'}
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Session Form */}
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    if (editingSession) {
                      updateSessionMutation.mutate({
                        sessionId: editingSession.id,
                        sessionData: {
                          startDateTime: sessionForm.startDateTime,
                          endDateTime: sessionForm.endDateTime,
                          maxStudents: parseInt(sessionForm.availableSpots),
                          notes: sessionForm.sessionNotes,
                        },
                      });
                    } else {
                      // If recurring, save the recurrence rule + start time on the
                      // class first so the generator produces future sessions. Use a
                      // direct PATCH-style update so the standard class fields aren't
                      // overwritten by the full-form transform.
                      if (sessionForm.isRecurring && sessionForm.startDateTime) {
                        // Wall-clock time as typed (local), not UTC — the
                        // generator resolves the offset per date for DST.
                        const start = new Date(sessionForm.startDateTime);
                        const hh = String(start.getHours()).padStart(2, '0');
                        const mm = String(start.getMinutes()).padStart(2, '0');
                        void apiRequest('PUT', `/api/admin/classes/${managingSessionsFor!.id}`, {
                          recurrenceRule: buildRRule(
                            sessionForm.recurrenceFreq,
                            sessionForm.recurrenceDays
                          ),
                          recurrenceStartTime: `${hh}:${mm}`,
                          recurrenceEndsAt: sessionForm.recurrenceEndsAt
                            ? new Date(sessionForm.recurrenceEndsAt).toISOString()
                            : null,
                        }).then(() => {
                          queryClient.invalidateQueries({
                            queryKey: [`/api/admin/classes/${managingSessionsFor!.id}/sessions`],
                          });
                        });
                      }
                      createSessionMutation.mutate({
                        classId: managingSessionsFor!.id,
                        sessionData: {
                          startDateTime: sessionForm.startDateTime,
                          endDateTime: sessionForm.endDateTime,
                          maxStudents: parseInt(sessionForm.availableSpots),
                          notes: sessionForm.sessionNotes,
                        },
                      });
                    }
                  }}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <DateTimePicker
                      id="startDateTime"
                      label="Start Date & Time"
                      value={sessionForm.startDateTime}
                      onChange={value => {
                        const newSessionForm = {
                          ...sessionForm,
                          startDateTime: value,
                        };

                        // Auto-populate end time only when creating new sessions (not editing)
                        if (!editingSession && value && managingSessionsFor) {
                          const startDate = new Date(value);
                          if (!isNaN(startDate.getTime()) && managingSessionsFor.duration) {
                            // Use the class duration in minutes to calculate end time
                            const durationMinutes =
                              (typeof managingSessionsFor.duration === 'number'
                                ? managingSessionsFor.duration
                                : parseInt(String(managingSessionsFor.duration))) || 60;
                            const endDate = new Date(
                              startDate.getTime() + durationMinutes * 60 * 1000
                            );
                            const endDateTimeString = utcToLocalDatetimeString(
                              endDate.toISOString()
                            );
                            newSessionForm.endDateTime = endDateTimeString;
                          }
                        }

                        setSessionForm(newSessionForm);
                      }}
                      placeholder="Select start date and time"
                      required
                    />
                  </div>
                  <div>
                    <DateTimePicker
                      id="endDateTime"
                      label={`End Date & Time ${!editingSession && managingSessionsFor ? '(calculated from class duration)' : ''}`}
                      value={sessionForm.endDateTime}
                      onChange={value => setSessionForm({ ...sessionForm, endDateTime: value })}
                      readOnly={!editingSession && !!managingSessionsFor}
                      className={!editingSession && managingSessionsFor ? 'bg-muted' : ''}
                      placeholder="Select end date and time"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="availableSpots">Available Spots *</Label>
                    <Input
                      id="availableSpots"
                      type="number"
                      min="1"
                      value={sessionForm.availableSpots}
                      onChange={e =>
                        setSessionForm({ ...sessionForm, availableSpots: e.target.value })
                      }
                      placeholder="12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="sessionNotes">Session Notes</Label>
                    <Input
                      id="sessionNotes"
                      value={sessionForm.sessionNotes}
                      onChange={e =>
                        setSessionForm({ ...sessionForm, sessionNotes: e.target.value })
                      }
                      placeholder="Optional notes"
                    />
                  </div>
                  {!editingSession && (
                    <div className="col-span-2 space-y-3 border rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="admin-session-recurring"
                          title="Recurring schedule"
                          checked={sessionForm.isRecurring}
                          onChange={e =>
                            setSessionForm(s => ({ ...s, isRecurring: e.target.checked }))
                          }
                          className="h-4 w-4"
                        />
                        <Label
                          htmlFor="admin-session-recurring"
                          className="cursor-pointer font-medium"
                        >
                          Recurring schedule
                        </Label>
                      </div>
                      {sessionForm.isRecurring && (
                        <div className="space-y-3 pt-1">
                          <p className="text-xs text-muted-foreground">
                            Creates this session and generates future ones at the same time.
                          </p>
                          <div>
                            <Label>Frequency</Label>
                            <Select
                              value={sessionForm.recurrenceFreq}
                              onValueChange={v =>
                                setSessionForm(s => ({
                                  ...s,
                                  recurrenceFreq: v as RecurrenceFreq,
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {sessionForm.recurrenceFreq !== 'monthly' && (
                            <div>
                              <Label>Days</Label>
                              <div className="flex gap-1 flex-wrap pt-1">
                                {WEEKDAY_CODES.map(day => (
                                  <button
                                    key={day}
                                    type="button"
                                    onClick={() =>
                                      setSessionForm(s => ({
                                        ...s,
                                        recurrenceDays: s.recurrenceDays.includes(day)
                                          ? s.recurrenceDays.filter(d => d !== day)
                                          : [...s.recurrenceDays, day],
                                      }))
                                    }
                                    className={`px-2 py-1 rounded text-xs border ${
                                      sessionForm.recurrenceDays.includes(day)
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-background text-foreground border-input'
                                    }`}
                                  >
                                    {WEEKDAY_LABELS[day]}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div>
                            <Label htmlFor="admin-session-recurrence-ends">
                              End date (optional)
                            </Label>
                            <Input
                              id="admin-session-recurrence-ends"
                              type="date"
                              value={sessionForm.recurrenceEndsAt}
                              onChange={e =>
                                setSessionForm(s => ({
                                  ...s,
                                  recurrenceEndsAt: e.target.value,
                                }))
                              }
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Leave blank to generate sessions on a rolling 8-week window.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="col-span-2 flex gap-2">
                    <Button
                      type="submit"
                      disabled={createSessionMutation.isPending || updateSessionMutation.isPending}
                    >
                      {createSessionMutation.isPending || updateSessionMutation.isPending
                        ? 'Saving...'
                        : editingSession
                          ? 'Update Session'
                          : 'Create Session'}
                    </Button>
                    {editingSession && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingSession(null);
                          setSessionForm({
                            startDateTime: '',
                            endDateTime: '',
                            availableSpots: '',
                            sessionNotes: '',
                            isRecurring: false,
                            recurrenceFreq: 'weekly',
                            recurrenceDays: [],
                            recurrenceEndsAt: '',
                          });
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </div>
                </form>

                {/* Sessions List */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Existing Sessions</h3>
                  {Array.isArray(sessions) && sessions.length > 0 ? (
                    <div className="space-y-2">
                      {sessions.map((session: any) => (
                        <div
                          key={session.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <p className="font-medium">
                              {formatDateWithTimezone(session.startDateTime, {
                                includeTime: true,
                              })}{' '}
                              - {formatDateWithTimezone(session.endDateTime, { includeTime: true })}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              <span className="font-medium">{session.enrolledCount || 0}</span>{' '}
                              enrolled /{' '}
                              <span className="font-medium">
                                {session.maxStudents || session.availableSpots}
                              </span>{' '}
                              total /{' '}
                              <span className="font-medium">
                                {(session.maxStudents || session.availableSpots) -
                                  (session.enrolledCount || 0)}
                              </span>{' '}
                              remaining
                              {session.notes && ` | Notes: ${session.notes}`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingSession(session);
                                setSessionForm({
                                  startDateTime: utcToLocalDatetimeString(session.startDateTime),
                                  endDateTime: utcToLocalDatetimeString(session.endDateTime),
                                  availableSpots: (
                                    session.maxStudents ||
                                    session.availableSpots ||
                                    ''
                                  ).toString(),
                                  sessionNotes: session.notes || session.sessionNotes || '',
                                  isRecurring: false,
                                  recurrenceFreq: 'weekly',
                                  recurrenceDays: [],
                                  recurrenceEndsAt: '',
                                });
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setShowDeleteConfirm({
                                  type: 'session',
                                  id: session.id,
                                  name: `${formatDateWithTimezone(session.startDateTime, { includeTime: true })}`,
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      No sessions found for this class
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setManagingSessionsFor(null)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={!!showDeleteConfirm}
            onOpenChange={open => !open && setShowDeleteConfirm(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete the {showDeleteConfirm?.type} "
                  {showDeleteConfirm?.name}"? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (showDeleteConfirm?.type === 'class') {
                      deleteClassMutation.mutate(showDeleteConfirm.id);
                    } else if (showDeleteConfirm?.type === 'session') {
                      deleteSessionMutation.mutate(showDeleteConfirm.id);
                    }
                    setShowDeleteConfirm(null);
                  }}
                  disabled={deleteClassMutation.isPending || deleteSessionMutation.isPending}
                >
                  {deleteClassMutation.isPending || deleteSessionMutation.isPending
                    ? 'Deleting...'
                    : 'Delete'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-6">
          <h2 className="text-2xl font-semibold">All Creator Schedules</h2>

          <Card>
            <CardHeader>
              <CardTitle>Upcoming Class Sessions</CardTitle>
              <CardDescription>View all upcoming class sessions from all creators</CardDescription>
            </CardHeader>
            <CardContent>
              {scheduleLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : adminSchedule && Array.isArray(adminSchedule) && adminSchedule.length > 0 ? (
                <div className="space-y-4">
                  {adminSchedule.map((session: any) => (
                    <div
                      key={session.sessionId}
                      className="border rounded-lg p-4"
                      data-testid={`admin-schedule-session-${session.sessionId}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-primary">Class Name</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-schedule-class-name-${session.sessionId}`}
                              >
                                {session.classTitle}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Creator</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-schedule-creator-${session.sessionId}`}
                              >
                                {session.creatorName ||
                                  `${session.creatorFirstName} ${session.creatorLastName}`.trim()}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Date</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-schedule-date-${session.sessionId}`}
                              >
                                {formatDateWithTimezone(session.startDateTime, {
                                  dateOnly: true,
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Time</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-schedule-time-${session.sessionId}`}
                              >
                                {formatTimeRange(session.startDateTime, session.endDateTime)}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Students</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-schedule-students-${session.sessionId}`}
                              >
                                {session.enrolledCount} enrolled of {session.maxStudents} spots
                              </p>
                            </div>
                          </div>
                          {session.notes && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="font-medium text-primary text-sm">Session Notes</p>
                              <p
                                className="text-muted-foreground text-sm"
                                data-testid={`admin-schedule-notes-${session.sessionId}`}
                              >
                                {session.notes}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex flex-col gap-2 items-end">
                          <Badge
                            variant={
                              session.enrolledCount === session.availableSpots
                                ? 'destructive'
                                : 'outline'
                            }
                            data-testid={`admin-schedule-status-${session.sessionId}`}
                          >
                            {session.enrolledCount === session.availableSpots
                              ? 'Full'
                              : 'Available'}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setViewingEnrollments({
                                sessionId: session.sessionId,
                                className: session.classTitle,
                                startDateTime: session.startDateTime,
                                endDateTime: session.endDateTime,
                                maxStudents: session.maxStudents,
                                enrolledCount: session.enrolledCount,
                              });
                            }}
                            data-testid={`button-view-enrollments-${session.sessionId}`}
                          >
                            <Eye size={14} className="mr-1" />
                            View Students
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-admin-scheduled-sessions">
                  <p className="text-muted-foreground">
                    No upcoming class sessions scheduled across all creators.
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sessions will appear here once creators add them to their classes.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="archive" className="space-y-6">
          <h2 className="text-2xl font-semibold">Archive - Completed Sessions</h2>

          <Card>
            <CardHeader>
              <CardTitle>Completed Class Sessions</CardTitle>
              <CardDescription>
                View all completed class sessions from all creators and their enrolled students
              </CardDescription>
            </CardHeader>
            <CardContent>
              {archiveLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : adminArchive && Array.isArray(adminArchive) && adminArchive.length > 0 ? (
                <div className="space-y-4">
                  {adminArchive.map((session: any) => (
                    <div
                      key={session.sessionId}
                      className="border rounded-lg p-4"
                      data-testid={`admin-archive-session-${session.sessionId}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-primary">Class Name</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-archive-class-name-${session.sessionId}`}
                              >
                                {session.classTitle}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Creator</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-archive-creator-${session.sessionId}`}
                              >
                                {session.creatorName ||
                                  `${session.creatorFirstName} ${session.creatorLastName}`.trim()}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Date</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-archive-date-${session.sessionId}`}
                              >
                                {formatDateWithTimezone(session.startDateTime, {
                                  dateOnly: true,
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Time</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-archive-time-${session.sessionId}`}
                              >
                                {formatTimeRange(session.startDateTime, session.endDateTime)}
                              </p>
                            </div>
                            <div>
                              <p className="font-medium text-primary">Students</p>
                              <p
                                className="text-foreground"
                                data-testid={`admin-archive-students-${session.sessionId}`}
                              >
                                {session.enrolledCount} enrolled of {session.maxStudents} spots
                              </p>
                            </div>
                          </div>
                          {session.notes && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="font-medium text-primary text-sm">Session Notes</p>
                              <p
                                className="text-muted-foreground text-sm"
                                data-testid={`admin-archive-notes-${session.sessionId}`}
                              >
                                {session.notes}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex flex-col gap-2 items-end">
                          <Badge
                            variant="secondary"
                            data-testid={`admin-archive-status-${session.sessionId}`}
                          >
                            Completed
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setViewingEnrollments({
                                sessionId: session.sessionId,
                                className: session.classTitle,
                                startDateTime: session.startDateTime,
                                endDateTime: session.endDateTime,
                                maxStudents: session.maxStudents,
                                enrolledCount: session.enrolledCount,
                              });
                            }}
                            data-testid={`button-view-archive-enrollments-${session.sessionId}`}
                          >
                            <Eye size={14} className="mr-1" />
                            View Students
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-admin-archived-sessions">
                  <p className="text-muted-foreground">No completed class sessions yet.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Completed sessions from all creators will appear here after they end.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Enrollment Details Dialog */}
      <Dialog open={!!viewingEnrollments} onOpenChange={() => setViewingEnrollments(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Student Enrollments - {viewingEnrollments?.className}</DialogTitle>
            <DialogDescription>
              View contact information and order details for students enrolled in this session.
            </DialogDescription>
          </DialogHeader>

          {viewingEnrollments && (
            <div className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm bg-muted p-4 rounded-lg">
                <div>
                  <p className="font-medium text-primary">Session Date</p>
                  <p className="text-foreground">
                    {formatDateWithTimezone(viewingEnrollments.startDateTime, { dateOnly: true })}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-primary">Session Time</p>
                  <p className="text-foreground">
                    {formatTimeRange(
                      viewingEnrollments.startDateTime,
                      viewingEnrollments.endDateTime
                    )}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-primary">Total Spots</p>
                  <p className="text-foreground">{viewingEnrollments.maxStudents}</p>
                </div>
                <div>
                  <p className="font-medium text-primary">Students Enrolled</p>
                  <p className="text-foreground">{viewingEnrollments.enrolledCount}</p>
                </div>
              </div>

              {enrollmentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : sessionEnrollments &&
                Array.isArray(sessionEnrollments) &&
                sessionEnrollments.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Enrolled Students</h3>
                  {sessionEnrollments.map((enrollment: any) => (
                    <div
                      key={enrollment.id}
                      className="border rounded-lg p-4"
                      data-testid={`enrollment-detail-${enrollment.id}`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4
                            className="font-semibold text-base mb-2"
                            data-testid={`student-name-${enrollment.id}`}
                          >
                            {enrollment.studentFirstName} {enrollment.studentLastName}
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Mail size={14} className="text-muted-foreground" />
                              <span data-testid={`student-email-${enrollment.id}`}>
                                {enrollment.studentEmail}
                              </span>
                            </div>
                            {enrollment.studentPhone && (
                              <div className="flex items-center gap-2">
                                <Phone size={14} className="text-muted-foreground" />
                                <span data-testid={`student-phone-${enrollment.id}`}>
                                  {enrollment.studentPhone}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <p className="font-medium text-primary">Tickets Purchased</p>
                            <p
                              className="text-foreground"
                              data-testid={`ticket-count-${enrollment.id}`}
                            >
                              {enrollment.ticketCount || 1}{' '}
                              {(enrollment.ticketCount || 1) === 1 ? 'ticket' : 'tickets'}
                            </p>
                          </div>
                          {Array.isArray(enrollment.guestNames) &&
                            enrollment.guestNames.length > 0 && (
                              <div>
                                <p className="font-medium text-primary">Guest Names</p>
                                <ul className="text-sm text-muted-foreground space-y-1">
                                  {enrollment.guestNames.map((guestName: string) => (
                                    <li key={`${enrollment.id}-${guestName}`}>{guestName}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          <div>
                            <p className="font-medium text-primary">Order Total</p>
                            <p
                              className="text-foreground font-semibold"
                              data-testid={`order-total-${enrollment.id}`}
                            >
                              {enrollment.totalAmount > 0
                                ? `$${enrollment.totalAmount.toFixed(2)}`
                                : 'FREE'}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium text-primary">Enrollment Status</p>
                            <Badge
                              variant={
                                enrollment.status === 'confirmed'
                                  ? 'default'
                                  : enrollment.status === 'refunded' ||
                                      enrollment.status === 'canceled'
                                    ? 'destructive'
                                    : 'secondary'
                              }
                              data-testid={`enrollment-status-${enrollment.id}`}
                            >
                              {enrollment.status}
                            </Badge>
                          </div>
                          <div>
                            <p className="font-medium text-primary">Enrolled Date</p>
                            <p
                              className="text-muted-foreground text-sm"
                              data-testid={`enrollment-date-${enrollment.id}`}
                            >
                              {formatDateCompact(enrollment.enrolledAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-session-enrollments">
                  <p className="text-muted-foreground">No students enrolled in this session yet.</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setViewingEnrollments(null)}
              data-testid="button-close-enrollments"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ClassDashboard() {
  return (
    <PublicShell routeKey="class-dashboard">
      <AdminClassManagement />
    </PublicShell>
  );
}
